import { reportBug, saveBugSolution } from './cloudService';
import { suggestBugFix } from './geminiService';

// Central bug capture. Everything here is fire-and-forget: a failure inside the
// reporter must never crash the app or re-trigger itself (recursion guard,
// throttle, session cap, ignore list).

const THROTTLE_MS = 5 * 60 * 1000;   // same fingerprint: max 1 report per 5 min per session
const MAX_REPORTS_PER_SESSION = 20;  // hard cap against error loops

const reportedAt = new Map<string, number>();
let sessionReportCount = 0;
let capturing = false;
let initialized = false;

// Errors that must never reach the reporter (noise or self-inflicted loops)
const IGNORE_PATTERNS = [
    'Supabase is not configured', // supabaseClient mock rejection would loop via unhandledrejection
    'ResizeObserver loop',
    'Script error.',              // opaque cross-origin errors, no signal
    'InvalidJWTToken',            // realtime-js heartbeat rejects unawaited on expired JWT after
                                  // standby; auth refresh + channel recovery self-heal, pure noise
];

export interface CaptureInput {
    message: string;
    stack?: string;
    componentStack?: string;
    source: 'boundary' | 'window' | 'rejection';
}

const buildFingerprint = (message: string, stack?: string): string => {
    const firstStackLine = (stack || '')
        .split('\n')
        .map(l => l.trim())
        .find(l => l.startsWith('at ') || l.includes('@')) || '';
    return `${message.slice(0, 200)}|${firstStackLine.slice(0, 200)}`;
};

const generateSolution = async (reportId: string, input: CaptureInput): Promise<void> => {
    try {
        const solution = await suggestBugFix({
            message: input.message,
            stack: input.stack,
            componentStack: input.componentStack,
            source: input.source,
            appContext: window.location.hash || window.location.pathname
        });
        await saveBugSolution(reportId, solution || '', solution ? 'done' : 'failed');
    } catch (e) {
        console.warn('Bug solution generation failed:', e);
        try { await saveBugSolution(reportId, '', 'failed'); } catch { /* ignore */ }
    }
};

export const captureBug = (input: CaptureInput): void => {
    try {
        if (capturing) return;
        const message = (input.message || '').trim();
        if (!message) return;
        if (IGNORE_PATTERNS.some(p => message.includes(p))) return;
        if (sessionReportCount >= MAX_REPORTS_PER_SESSION) return;

        const fingerprint = buildFingerprint(message, input.stack);
        const last = reportedAt.get(fingerprint);
        if (last && Date.now() - last < THROTTLE_MS) return;
        reportedAt.set(fingerprint, Date.now());
        sessionReportCount++;

        capturing = true;
        void (async () => {
            try {
                const row = await reportBug({
                    fingerprint,
                    message,
                    stack: input.stack,
                    componentStack: input.componentStack,
                    source: input.source,
                    appContext: window.location.hash || window.location.pathname,
                    userAgent: navigator.userAgent
                });
                if (row && (row.is_new || !row.has_solution)) {
                    // Fire-and-forget: AI failure must not affect the saved report
                    void generateSolution(row.report_id, input);
                }
            } catch (e) {
                console.warn('Bug capture failed:', e);
            } finally {
                capturing = false;
            }
        })();
    } catch {
        capturing = false;
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// Der Hintergrundkanal — Integritaetsmeldungen ohne Sperre und ohne KI
// ═══════════════════════════════════════════════════════════════════════════
//
// Warum ein eigener Kanal: Integritaetsmeldungen (Schatten am Tor in
// allocationGateShadow.ts, gescheiterte Journalspiegel in serviceJournal.ts)
// liefen ueber `captureBug`. Zwei Folgen, beide im Review 10/2026 gefunden:
//  1. `captureBug` VERWIRFT jede Meldung, solange eine andere unterwegs ist
//     (`capturing`). Eine Hintergrundmeldung konnte so den echten Fehler
//     eines Nutzers verdraengen, der im selben Augenblick auftrat — und
//     umgekehrt ging die Integritaetsmeldung verloren. Ab S5a Stufe 3 sperrt
//     eine Abweichung die Rechnung; ihre Meldung MUSS ankommen.
//  2. Jeder neue Fingerabdruck stiess einen Gemini-Loesungsvorschlag an.
//     Fuer maschinell erzeugte Abweichungslisten ist das reine Kosten.
//
// Der Kanal schreibt ueber DIESELBE RPC (`report_bug`) mit denselben Spalten
// und einem Fingerabdruck nach derselben Regel (`buildFingerprint`) —
// `bug_reports` sieht gleich aus, das Hochzaehlen je Fingerabdruck
// funktioniert wie bisher. Drossel und
// Sitzungsdeckel sind EIGENE: Hintergrundmeldungen verbrauchen nie das
// Kontingent der Nutzerfehler und umgekehrt.

/**
 * Kennzeichen am Anfang der Meldung. `source` kann es nicht tragen: die
 * Spalte hat einen CHECK auf boundary/window/rejection, und neues SQL gibt es
 * fuer diesen Kanal nicht. Vorn in der Meldung ist es im Admin-Bereich sofort
 * sichtbar und per `message like '[Hintergrund]%'` abfragbar; Teil des
 * Fingerabdrucks ist es auch, ein Nutzerfehler faellt also nie mit einer
 * Hintergrundmeldung zusammen.
 */
export const HINTERGRUND_KENNZEICHEN = '[Hintergrund]';

const hintergrundGemeldetAm = new Map<string, number>();
let hintergrundMeldungenInSitzung = 0;

export interface HintergrundMeldung {
    message: string;
    stack?: string;
    /** Nur die drei Werte, die der CHECK auf `bug_reports.source` zulaesst. */
    source?: CaptureInput['source'];
}

/**
 * Eine Integritaetsmeldung nach `bug_reports` schreiben — ohne die Sperre von
 * `captureBug`, ohne Gemini.
 *
 * Wirft nie und lehnt nie ab: das Versprechen erfuellt sich, sobald die
 * Meldung geschrieben ist oder feststeht, dass sie nicht geschrieben wird. Der
 * Aufrufer darf es abwarten oder mit `void` laufen lassen. Nur im Browser —
 * im Test und im Nachtlauf gibt es kein `window`, und dort soll auch nichts
 * in `bug_reports` landen.
 */
export const meldeImHintergrund = async (input: HintergrundMeldung): Promise<void> => {
    // Die Reservierung (Drossel + Sitzungsdeckel), solange die Uebertragung
    // laeuft — scheitert sie, wird sie unten zurueckgegeben.
    let reserviert: { fingerprint: string; am: number } | null = null;
    const freigeben = () => {
        if (!reserviert) return;
        // Nur die EIGENE Reservierung: hat inzwischen eine spaetere Meldung
        // denselben Fingerabdruck gesetzt, gehoert der Eintrag ihr.
        if (hintergrundGemeldetAm.get(reserviert.fingerprint) === reserviert.am) {
            hintergrundGemeldetAm.delete(reserviert.fingerprint);
        }
        hintergrundMeldungenInSitzung = Math.max(0, hintergrundMeldungenInSitzung - 1);
        reserviert = null;
    };
    try {
        if (typeof window === 'undefined') return;
        const roh = (input?.message || '').trim();
        if (!roh) return;
        // Keine Ignorierliste wie bei `captureBug`: die filtert Rauschen der
        // globalen Fehlerhaken. Hier meldet nur Code, der etwas melden WILL.
        if (hintergrundMeldungenInSitzung >= MAX_REPORTS_PER_SESSION) return;

        const message = `${HINTERGRUND_KENNZEICHEN} ${roh}`;
        const fingerprint = buildFingerprint(message, input.stack);
        const jetzt = Date.now();
        const zuletzt = hintergrundGemeldetAm.get(fingerprint);
        if (zuletzt !== undefined && jetzt - zuletzt < THROTTLE_MS) return;
        // VOR dem ersten `await` vermerkt: zwei gleichzeitige gleiche Meldungen
        // gehen so nur einmal hinaus, zwei verschiedene beide.
        hintergrundGemeldetAm.set(fingerprint, jetzt);
        hintergrundMeldungenInSitzung++;
        reserviert = { fingerprint, am: jetzt };

        const row = await reportBug({
            fingerprint,
            message,
            stack: input.stack,
            source: input.source ?? 'window',
            appContext: window.location?.hash || window.location?.pathname,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        });
        if (!row) {
            // `reportBug` schluckt Netz- und Serverfehler und liefert dann
            // null. Ohne Rueckgabe verbrauchte jeder Fehlschlag Drossel und
            // Deckel — nach 20 Netzaussetzern bliebe der Kanal bis zum
            // Neuladen stumm, auch wenn das Netz laengst zurueck ist
            // (Codex-Review S5a, 03.10.2026, P2). Die naechste gleiche Meldung
            // versucht es dann erneut.
            freigeben();
            return;
        }
        reserviert = null;
        if (row.is_new || !row.has_solution) {
            // Kein Gemini — aber auch kein ewiges „Loesungsvorschlag wird
            // erstellt…" im Admin-Bereich: `report_bug` legt die Zeile mit
            // `ai_solution_status = 'pending'` an, und ohne diesen Schritt
            // bliebe sie dort stehen. 'failed' zeigt „Keine Loesung
            // verfuegbar"; per Knopf laesst sich trotzdem einer erzeugen.
            await saveBugSolution(row.report_id, '', 'failed');
        }
    } catch (e) {
        freigeben();
        try { console.warn('Hintergrundmeldung fehlgeschlagen:', e); } catch { /* ignore */ }
    }
};

export const initGlobalBugCapture = (): void => {
    if (initialized || typeof window === 'undefined') return;
    initialized = true;

    window.addEventListener('error', (e: ErrorEvent) => {
        captureBug({
            message: e.message || String(e.error?.message || 'Unbekannter Fehler'),
            stack: e.error?.stack || `${e.filename || ''}:${e.lineno || 0}:${e.colno || 0}`,
            source: 'window'
        });
    });

    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
        const reason: any = e.reason;
        captureBug({
            message: reason?.message || (typeof reason === 'string' ? reason : 'Unbehandelte Promise-Ablehnung'),
            stack: reason?.stack,
            source: 'rejection'
        });
    });
};
